import { message } from 'antd';
import React, { useCallback, useEffect, useState } from 'react';
import config from '../../../config';
import { getAsset } from '../../../services/general';

const usePatientPhoto = ({
  form,
  patientToAdmission,
  setLoading,
  countryCode,
}) => {
  const [method, setMethod] = useState();
  const [visible, setVisible] = useState(false);
  const [imageSrc, setImageSrc] = useState(null);
  const webcamRef = React.useRef(null);

  const videoConstraints = {
    width: 741,
    height: 720,
  };

  const setPatientPhoto = useCallback((photo, isNew = true) => {
    setImageSrc(photo);
    if (form) {
      form.setFieldsValue({ pictureIdCard: { photo, isNew } });
    }
  }, [form]);

  useEffect(() => {
    if (patientToAdmission?.pictureIdCard) {
      setPatientPhoto(patientToAdmission.pictureIdCard.photo);
    }
  }, [patientToAdmission, setPatientPhoto]);

  useEffect(() => {
    if (countryCode === 'PE') {
      setMethod('webcam');
    }
  }, [countryCode]);

  const capture = useCallback(() => {
    if (imageSrc && !imageSrc.includes('missing')) {
      setVisible(false);
      return;
    }
    const imageFromCapture = webcamRef.current.getScreenshot();
    setPatientPhoto(imageFromCapture);
  }, [imageSrc, setPatientPhoto]);

  const resetImage = useCallback(() => {
    setPatientPhoto(null);
  }, [setPatientPhoto]);

  const openWebcam = useCallback(() => {
    setVisible(true);
    if (webcamRef.current) {
      const stream = webcamRef.current.stream;
      if (stream) {
        const tracks = stream.getTracks();
        tracks.forEach(track => {
          track.stop();
        });
      }
    }
  }, [webcamRef]);

  const fetchPatientIdCard = useCallback(async (params) => {
    try {
      setLoading((loading) => { return { ...loading, idCard: true } });
      const response = await getAsset({ id: params.id, type: 'Patient', field: 'document' });
      setPatientPhoto(response.document_url, false);
    } catch (error) {
      console.error(error);
      message.error({
        content: 'No se pudo cargar la foto del paciente',
        style: {
          marginTop: "35vh",
          fontSize: 16,
        },
      });
      setPatientPhoto(null);
    } finally {
      setLoading((loading) => { return { ...loading, idCard: false } });
    }
  }, [setLoading, setPatientPhoto]);

  const openCameraToc = useCallback(() => {
    let url = config.validatorUrl;
    let params = '/TOC/documentPhoto';
    let target = '_blank';
    let options = `scrollbars=no,resizable=no,status=no,location=no,toolbar=no,menubar=no,width=0,height=0,left=-1000,top=-1000`;

    window.addEventListener('message', (event) => {
      if (event.origin.startsWith(url)) {
        setPatientPhoto(event.data);
        setMethod(null);
      }
    }, false);

    window.open(`${url}${params}`, target, options);
  }, [setPatientPhoto]);

  const selectMethod = (selectedMethod) => {
    setMethod(selectedMethod);
    if (selectedMethod === 'toc') {
      openCameraToc();
    } else {
      openWebcam();
    }
  }

  const openModal = useCallback(() => {
    setVisible(true);

    if (countryCode === 'PE') {
      openWebcam();
    }
  }, [countryCode, openWebcam]);

  const closeModal = useCallback(() => {
    setVisible(false);
    setMethod(countryCode === 'PE' ? 'webcam' : null);
    if (webcamRef.current) {
      const stream = webcamRef.current.stream;
      if (stream) {
        const tracks = stream.getTracks();
        tracks.forEach(track => {
          track.stop();
        });
      }
    }
  }, [countryCode]);

  return {
    visible,
    openModal,
    closeModal,
    capture,
    imageSrc,
    webcamRef,
    resetImage,
    videoConstraints,
    fetchPatientIdCard,
    method,
    selectMethod,
  };
};

export default usePatientPhoto;
